import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Icon from '../Icon/Icon.js';
import HtmlContent from '../Text/styledComponents/HtmlContent.js';

import { IRadioButtonsConfiguration, IRadioButtonsProps, IRadioButtonsState } from './models/index.js';
import RadioButtonInput from './styledComponents/RadioButtonInput.js';
import RadioButtonLabel from './styledComponents/RadioButtonLabel.js';
import RadioButtonLabelText from './styledComponents/RadioButtonLabelText.js';
import Wrapper from './styledComponents/Wrapper.js';
import { RADIO_BUTTONS_CATEGORY_KEY, RADIO_BUTTONS_COMPONENT_KEY } from './RadioButtons.meta.js';

export class RadioButtons extends ComponentBase<IRadioButtonsProps, IRadioButtonsState, IRadioButtonsConfiguration> {
	categoryKey = RADIO_BUTTONS_CATEGORY_KEY;
	componentKey = RADIO_BUTTONS_COMPONENT_KEY;

	value: any | null = null;

	constructor(props: IRadioButtonsProps) {
		super(props);

		this.value = props.value;

		makeObservable(this, {
			value: observable,
			setValue: action,
			handleOptionChange: action,
		});
	}

	componentDidUpdate(previousProps: IRadioButtonsProps) {
		const { value } = this.props;
		const valueChanged = value !== undefined && previousProps.value !== value;
		if (valueChanged) {
			this.setValue(value);
		}
	}

	setValue = (value: any | undefined) => {
		this.value = value;
	};

	handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const optionIndex = parseInt(event.target.value, 10);
		const { options = [] } = this.props;
		this.value = options.length > optionIndex ? options[optionIndex].value : undefined;
		const { onChange } = this.props;
		if (onChange) {
			onChange(this.value);
		}
	};

	render() {
		const {
			className,
			name = common.commonUtils.generateGuid(),
			options = [],
			labelPosition = 'after',
			disabled = false,
			selectedOptionStyle,
			selectedOptionIcon,
			hasHtmlContent,
		} = this.props;
		return (
			<Wrapper className={className}>
				{options.map((option, optionIndex) => {
					const { value, label } = option;
					const componentStyle = this.value === value ? selectedOptionStyle : {};
					const labelText = hasHtmlContent ? (
						<RadioButtonLabelText>
							<HtmlContent dangerouslySetInnerHTML={{ __html: label ? label : '' }} />
						</RadioButtonLabelText>
					) : (
						<RadioButtonLabelText>{label}</RadioButtonLabelText>
					);
					return (
						<RadioButtonLabel key={value} componentStyle={componentStyle}>
							{labelPosition === 'before' && labelText}
							<RadioButtonInput
								type="radio"
								name={name}
								value={optionIndex}
								checked={this.value === value}
								onChange={this.handleOptionChange}
								disabled={disabled}
							/>
							{labelPosition === 'after' && labelText}
							{selectedOptionIcon && this.value === value && <Icon {...selectedOptionIcon} />}
						</RadioButtonLabel>
					);
				})}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(RadioButtons), RADIO_BUTTONS_CATEGORY_KEY, RADIO_BUTTONS_COMPONENT_KEY);
